import { useMemo } from 'react';
import { prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';
import { TFormFieldLossLimit, TFormFieldBetLimit } from 'js/components/common/Form/types';
import { EDITABLE_DD_MODES } from 'js/components/common/Fields/EditableDropDown/constants';
import { RG_LIMIT_PREDEFINED_TYPE, RG_LIMIT_PERIOD_WITH_SESSION } from 'js/hooks/socket/hooks/useGetRGConfiguration/constants';

import * as Types from '../types';
import { IMS_UNLIMITED_AMOUNT } from '../constants';
import { useDynamicReducerSelector } from '../../../store';

export const useLimitsConfig = (field?: TFormFieldLossLimit | TFormFieldBetLimit) => {
    const translate = useTranslations('playtech.mobile.system.form.limits.');
    const limitsSettings = useDynamicReducerSelector(prop('limitsSettings'));
    const type = field?.type === FORM_FIELD_TYPE.BET_LIMITS ? 'bet' : 'loss';
    const typeLimitsSettings = limitsSettings[type];

    return useMemo((): Types.TLimitsConfig | undefined => {
        if (!typeLimitsSettings || !field) {
            return;
        }

        const {
            availablePeriods, predefinedLimits, predefinedType, noLimitsAllowed,
            sessionDefaultLimit = '', dailyDefaultLimit = '', weeklyDefaultLimit = '', monthlyDefaultLimit = ''
        } = typeLimitsSettings;
        const { isSessionEnabled, isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled } = field;

        let mode = EDITABLE_DD_MODES.ALL;

        if (predefinedType === RG_LIMIT_PREDEFINED_TYPE.FORCED) {
            mode = EDITABLE_DD_MODES.LIST_ONLY;
        } else if (!noLimitsAllowed && predefinedType === RG_LIMIT_PREDEFINED_TYPE.DISABLED) {
            mode = EDITABLE_DD_MODES.TEXT_ONLY;
        }

        return {
            ...typeLimitsSettings,
            mode,
            displayValuesMap: {
                0: '',
                ...noLimitsAllowed ? { [IMS_UNLIMITED_AMOUNT]: translate('unlimited') } : {}
            },
            sessionDefaultLimit,
            dailyDefaultLimit,
            weeklyDefaultLimit,
            monthlyDefaultLimit,
            isSessionEnabled: availablePeriods.includes(RG_LIMIT_PERIOD_WITH_SESSION.SESSION) && isSessionEnabled,
            isDailyEnabled: availablePeriods.includes(RG_LIMIT_PERIOD_WITH_SESSION.DAY) && isDailyEnabled,
            isWeeklyEnabled: availablePeriods.includes(RG_LIMIT_PERIOD_WITH_SESSION.WEEK) && isWeeklyEnabled,
            isMonthlyEnabled: availablePeriods.includes(RG_LIMIT_PERIOD_WITH_SESSION.MONTH) && isMonthlyEnabled,
            dataList: [
                ...(predefinedLimits || []).map(predefinedLimit => ({ value: predefinedLimit, displayValue: predefinedLimit })),
                ...noLimitsAllowed ? [{ value: IMS_UNLIMITED_AMOUNT, displayValue: translate('unlimited') }] : []
            ]
        };
    }, [translate, field, typeLimitsSettings]);
};
